import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, tap, delay, switchMap } from 'rxjs/operators';
import { ApprovalInstance, ApprovalStepInstance, ResolvedApprovalFlow, ResolvedApprovalStep, InitiatorInfo } from '../models/approval-flow.model';
import { ApprovalFlowDefinition, Role, Department, User, ApprovalStepDefinition, ApiApprovalFlowDefinitionsResponse  } from '../models/approval-flow-definition.model';
import { environment } from '../../../../services/global-environment';
import { AuthService } from '../../../../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApprovalService {
  private apiUrl = `${environment.apiUrl}/api/approval`;
  private departmentApiUrl = `${environment.apiUrl}/api/departments`;
  private userApiUrl = `${environment.apiUrl}/api/users`;

  constructor(private http: HttpClient, private authService: AuthService) { }


  // 修改: 將 getApprovalInstance 改名為 getApprovalById 以與 ApprovalActionComponent 使用一致
  getApprovalById(approvalId: string): Observable<ApprovalInstance | null> {
    return this.http.get<ApprovalInstance>(`${this.apiUrl}/${approvalId}`).pipe(
      catchError(error => {
        console.error('Error fetching approval instance', error);
        return of(null);
      })
    );
  }

  // 保留原方法名稱以向後兼容
  getApprovalInstance(approvalId: string): Observable<ApprovalInstance | null> {
    return this.getApprovalById(approvalId);
  }

  // 現有方法：獲取表單的簽核狀態
  getApprovalForForm(formId: string): Observable<ApprovalInstance | null> {
    return this.http.get<ApprovalInstance>(`${this.apiUrl}/form/${formId}`).pipe(
      catchError(error => {
        console.error('Error fetching approval for form', error);
        return of(null);
      })
    );
  }

  // 現有方法：執行簽核動作
  performApprovalAction(
    approvalId: string,
    action: 'approve' | 'reject',
    comment?: string,
    signature?: string | null
  ): Observable<ApprovalInstance | null> {
    return this.http.post<ApprovalInstance>(`${this.apiUrl}/${approvalId}/action`, {
      action,
      comment,
      signature
    }).pipe(
      catchError(error => {
        console.error('Error performing approval action', error);
        return of(null);
      })
    );
  }

  // 新增方法：獲取所有簽核流程定義
  getApprovalFlowDefinitions(companyid: string, assistantNo: string): Observable<ApiApprovalFlowDefinitionsResponse> {
    console.log('查詢簽核流程定義，參數:', { companyid, assistantNo });

    const params = {
      companyid: companyid,
      assistantNo: assistantNo
    };

    return this.http.get<ApiApprovalFlowDefinitionsResponse>(`${this.apiUrl}/flow-definitions`, { params }).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && Array.isArray(response.data)) {
          console.log('從 API 成功獲取簽核流程清單，數量:', response.data.length);
          return response;
        }

        console.warn('API 回應格式不正確:', response);
        // 回傳空結果但成功的格式
        return {
          success: true,
          data: [],
          total: 0
        };
      }),
      catchError(error => {
        console.error('獲取簽核流程定義失敗:', error);
        // 回傳失敗結果
        return of({
          success: false,
          data: [],
          total: 0
        });
      })
    );
  }

  // 新增方法：根據ID獲取簽核流程定義
  getApprovalFlowDefinitionById(id: string, companyid: string, assistantNo: string): Observable<ApprovalFlowDefinition | null> {
    console.log('查詢特定簽核流程定義，ID:', id, 'CompanyID:', companyid, 'AssistantNo:', assistantNo);

    const params = new HttpParams()
      .set('companyid', companyid)
      .set('assistantNo', assistantNo);

    return this.http.get<any>(`${this.apiUrl}/flow-definitions/${id}`, { params }).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response) {
          // 如果回應有 success 和 data 屬性（標準 API 格式）
          if (response.success && response.data) {
            console.log('從 API 成功獲取簽核流程:', response.data);
            return response.data as ApprovalFlowDefinition;
          }
          // 如果回應直接是簽核流程物件
          else if (response.id && response.name) {
            console.log('從 API 成功獲取簽核流程 (直接格式):', response);
            return response as ApprovalFlowDefinition;
          }
        }

        console.warn('API 回應格式不正確:', response);
        return null;
      }),
      catchError(error => {
        console.error('獲取簽核流程定義失敗:', error);
        return of(null);
      })
    );
  }

  // 新增方法：創建新的簽核流程定義
  createApprovalFlowDefinition(flowDef: ApprovalFlowDefinition): Observable<ApprovalFlowDefinition> {
    return this.http.post<any>(`${this.apiUrl}/flow-definitions`, flowDef).pipe(
      map(response => {
        // 處理 API 回應格式: { success: true, data: createdItem, message: "..." }
        if (response && response.success && response.data) {
          return response.data;
        }
        // 如果回應格式不符預期，則返回原始回應
        return response;
      }),
      catchError(error => {
        console.error('Error creating approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：更新簽核流程定義
  updateApprovalFlowDefinition(flowDef: ApprovalFlowDefinition): Observable<ApprovalFlowDefinition> {
    return this.http.put<any>(`${this.apiUrl}/flow-definitions/${flowDef.id}`, flowDef).pipe(
      map(response => {
        // 處理 API 回應格式: { success: true, data: updatedItem, message: "..." }
        if (response && response.success && response.data) {
          return response.data;
        }
        // 如果回應格式不符預期，則返回原始回應
        return response;
      }),
      catchError(error => {
        console.error('Error updating approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：刪除簽核流程定義
  deleteApprovalFlowDefinition(id: string, companyid: string, assistantNo: string): Observable<boolean> {
    // 建立查詢參數
    const params = new URLSearchParams({
      companyid: companyid,
      assistantNo: assistantNo
    });

    return this.http.delete<void>(`${this.apiUrl}/flow-definitions/${id}?${params.toString()}`).pipe(
      map(() => true),
      catchError(error => {
        console.error('Error deleting approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：啟動表單的簽核流程
  initiateApprovalFlow(formId: string, flowDefId: string): Observable<ApprovalInstance | null> {
    return this.http.post<ApprovalInstance>(`${this.apiUrl}/initiate`, {
      formId,
      flowDefinitionId: flowDefId
    }).pipe(
      catchError(error => {
        console.error('Error initiating approval flow', error);
        return of(null);
      })
    );
  }

  // 新增方法：解析表單的簽核流程
  resolveApprovalFlowForForm(formName: string, initiatorInfo: InitiatorInfo, companyid: string, assistantNo: string): Observable<ResolvedApprovalFlow> {
    console.log('開始解析簽核流程，表單名稱:', formName, '填寫人資訊:', initiatorInfo, '公司ID:', companyid, '助手編號:', assistantNo);

    // 使用真實 API 查找適用於此表單的簽核流程
    return this.getApprovalFlowDefinitions(companyid, assistantNo).pipe(
      switchMap(response => {
        if (!response.success || !response.data || response.data.length === 0) {
          console.warn('未找到任何簽核流程定義');
          const errorFlow: ResolvedApprovalFlow = {
            flowId: '',
            flowName: '無可用簽核流程',
            steps: [],
            hasErrors: true,
            errors: ['未找到適用的簽核流程定義']
          };
          return of(errorFlow);
        }

        const flows = response.data;
        console.log('從 API 獲取到簽核流程數量:', flows.length);

        // 根據表單名稱查找對應的簽核流程
        const matchedFlow = flows.find(flow =>
          flow.formTypes && flow.formTypes.some(formType =>
            formName.includes(formType) || formType === 'general'
          )
        );

        if (!matchedFlow) {
          // 如果找不到對應的流程，使用預設流程
          const defaultFlow = flows.find(flow => flow.formTypes && flow.formTypes.includes('general')) || flows[0];
          if (defaultFlow) {
            console.log('使用預設簽核流程:', defaultFlow.name);
            return this.resolveFlowSteps(defaultFlow, initiatorInfo, companyid, assistantNo);
          } else {
            // 完全沒有可用的流程
            const errorFlow: ResolvedApprovalFlow = {
              flowId: '',
              flowName: '無可用簽核流程',
              steps: [],
              hasErrors: true,
              errors: ['找不到適用的簽核流程']
            };
            return of(errorFlow);
          }
        }

        console.log('找到匹配的簽核流程:', matchedFlow.name);
        return this.resolveFlowSteps(matchedFlow, initiatorInfo, companyid, assistantNo);
      }),
      catchError(error => {
        console.error('解析簽核流程時發生錯誤:', error);
        const errorFlow: ResolvedApprovalFlow = {
          flowId: '',
          flowName: '解析失敗',
          steps: [],
          hasErrors: true,
          errors: ['解析簽核流程時發生錯誤: ' + (error.message || error)]
        };
        return of(errorFlow);
      })
    );
  }

  // 新增方法：解析簽核流程的各個步驟
  private resolveFlowSteps(flowDef: ApprovalFlowDefinition, initiatorInfo: InitiatorInfo, companyid: string, assistantNo: string): Observable<ResolvedApprovalFlow> {
    const resolvedFlow: ResolvedApprovalFlow = {
      flowId: flowDef.id || '',
      flowName: flowDef.name,
      steps: [],
      hasErrors: false,
      errors: []
    };

    // 獲取所有必要的資料（部門、角色、使用者）
    return forkJoin({
      departments: this.getDepartments(companyid, assistantNo),
      roles: this.getRoles(),
      users: this.getUsers(companyid)
    }).pipe(
      map(({ departments, roles, users }) => {
        // 解析每個步驟
        flowDef.steps.forEach((step, index) => {
          try {
            const resolvedStep = this.resolveSingleStep(
              step,
              index + 1,
              initiatorInfo,
              departments,
              roles,
              users
            );
            resolvedFlow.steps.push(resolvedStep);

            if (resolvedStep.error) {
              resolvedFlow.hasErrors = true;
              resolvedFlow.errors.push(resolvedStep.error);
            }
          } catch (error) {
            console.error(`解析步驟 ${index + 1} 時發生錯誤:`, error);
            const errorStep: ResolvedApprovalStep = {
              level: index + 1,
              departid: '',
              roleid: step.roleId || '',
              userid: '',
              stepName: step.name,
              approverName: '解析失敗',
              isDynamicDepartment: false,
              error: `解析步驟時發生錯誤: ${error}`
            };
            resolvedFlow.steps.push(errorStep);
            resolvedFlow.hasErrors = true;
            resolvedFlow.errors.push(errorStep.error!);
          }
        });

        return resolvedFlow;
      })
    );
  }

  // 新增方法：解析單個簽核步驟
  private resolveSingleStep(
    step: ApprovalStepDefinition,
    level: number,
    initiatorInfo: InitiatorInfo,
    departments: Department[],
    roles: Role[],
    users: User[]
  ): ResolvedApprovalStep {
    const resolvedStep: ResolvedApprovalStep = {
      level: level,
      departid: '',
      roleid: step.roleId || '',
      userid: '',
      stepName: step.name,
      approverName: '待解析',
      isDynamicDepartment: step.isDynamicDepartment || false
    };

    try {
      // 解析部門
      let targetDepartmentId = step.departmentId || '';

      console.log('解析步驟:', step.name, '部門ID:', targetDepartmentId, '填寫人資訊:', initiatorInfo);

      if (step.isDynamicDepartment) {

        console.log("處理動態部門");

        // 處理動態部門
        switch (step.departmentId) {
          case 'submitter_dept':
            targetDepartmentId = initiatorInfo.departmentId;
            console.log("跟填寫人同部門:", targetDepartmentId);
            break;
          case 'parent_dept':
            // 查找填寫人部門的上級部門（這裡簡化處理，實際應該有部門層級結構）
            //TODO:要修改
            const parentDept = departments.find(d => d.id === initiatorInfo.departmentId + '_parent');
            targetDepartmentId = parentDept ? parentDept.id : initiatorInfo.departmentId;

            console.log("填寫人的上級部門:", targetDepartmentId);
            break;
          default:
            targetDepartmentId = step.departmentId || '';
        }
      }

      resolvedStep.departid = targetDepartmentId;

      // 根據簽核類型解析簽核人
      if (step.approverType === 'specific_user' && step.userId) {

        console.log("處理特定使用者簽核人");

        // 特定使用者
        const user = users.find(u => u.id === step.userId);
        if (user) {
          resolvedStep.userid = user.id;
          resolvedStep.approverName = user.name;
        } else {
          resolvedStep.error = `找不到指定的使用者: ${step.userId}`;
          resolvedStep.approverName = '找不到使用者';
        }


        console.log("處理後結果:", resolvedStep);


      } else if (step.approverType === 'role_based' && step.roleId) {

        console.log("處理基於角色的簽核人");

        // 基於角色的簽核人
        const role = roles.find(r => r.id === step.roleId);
        if (role) {
          //TODO:這邊要處理
          // 查找具有指定角色且在目標部門的使用者
          let approver = users.find(u =>
            u.roleId === step.roleId &&
            (targetDepartmentId === '' || u.departmentId === targetDepartmentId)
          );

          if (approver) {
            resolvedStep.userid = approver.id;
            resolvedStep.approverName = approver.name;
          } else {
            if (step.isDynamicDepartment) {
              resolvedStep.approverName = '動態分配';
              // 為動態部門提供更友善的錯誤訊息
              const deptName = departments.find(d => d.id === targetDepartmentId)?.name || targetDepartmentId;
              resolvedStep.error = `將在填寫時自動分配到 "${deptName}" 的 "${role.name}"`;
            } else {
              resolvedStep.error = `找不到具有角色 "${role.name}" 的簽核人`;
              resolvedStep.approverName = '找不到簽核人';
            }
          }
        } else {
          resolvedStep.error = `找不到指定的角色: ${step.roleId}`;
          resolvedStep.approverName = '找不到角色';
        }
      } else {
        resolvedStep.error = '簽核設定不完整';
        resolvedStep.approverName = '設定錯誤';
      }

    } catch (error) {
      resolvedStep.error = `解析步驟時發生異常: ${error}`;
      resolvedStep.approverName = '解析失敗';
    }

    return resolvedStep;
  }

  // 新增方法：解析動態簽核人（為向後相容保留的方法）
  resolveDynamicApprover(step: ApprovalStepDefinition, initiatorInfo: InitiatorInfo, companyid: string, assistantNo: string): Observable<ResolvedApprovalStep> {
    return forkJoin({
      departments: this.getDepartments(companyid, assistantNo),
      roles: this.getRoles(),
      users: this.getUsers(companyid)
    }).pipe(
      map(({ departments, roles, users }) => {
        return this.resolveSingleStep(step, 1, initiatorInfo, departments, roles, users);
      })
    );
  }

  // 新增方法：取得部門資料
  getDepartments(companyid: string, assistantNo: string): Observable<Department[]> {
    console.log('查詢部門資料，參數:', { companyid, assistantNo });

    return this.http.get<any>(`${this.departmentApiUrl}/${companyid}/${assistantNo}`).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && response.data) {
          console.log('從 API 成功獲取部門資料:', response.data);

          // 將樹狀結構轉換為平面的部門陣列
          const departments = this.extractDepartmentsFromTree(response.data);
          console.log('轉換後的部門陣列:', departments);
          return departments;
        }

        console.warn('API 回應格式不正確或無資料:', response);
        return [];
      }),
      catchError(error => {
        console.error('獲取部門資料失敗:', error);
        return of([]);
      })
    );
  }

  // 私有方法：從樹狀結構中提取所有部門
  private extractDepartmentsFromTree(data: any): Department[] {
    const departments: Department[] = [];

    // 遞迴函數來提取部門
    const extractDepartments = (node: any, parentId?: string, level: number = 0) => {
      if (node.type === 'department') {
        const department: Department = {
          id: node.id,
          name: node.name,
          level: level,
          parentId: parentId
        };
        departments.push(department);
      }

      // 如果是公司根節點，也視為部門
      if (node.type === 'company') {
        const department: Department = {
          id: node.id,
          name: node.name,
          level: level,
          parentId: parentId
        };
        departments.push(department);
      }

      // 遞迴處理子節點
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => {
          extractDepartments(child, node.type === 'department' || node.type === 'company' ? node.id : parentId, level + 1);
        });
      }
    };

    extractDepartments(data);
    return departments;
  }

  // 新增方法：取得角色資料
  getRoles(): Observable<Role[]> {
    console.log('取得角色資料從 AuthService');

    // 從 AuthService 取得角色選項
    const roleOptions = this.authService.roleOptions;

    // 將 RoleOption 轉換為 Role 格式
    const roles: Role[] = roleOptions.map(option => ({
      id: option.value,
      name: option.label,
      departmentId: null, // 角色不綁定特定部門
      isCommonRole: true  // 標記為通用角色
    }));

    console.log('轉換後的角色資料:', roles);
    return of(roles);
  }

  // 新增方法：取得使用者資料
  getUsers(companyid: string): Observable<User[]> {
    console.log('查詢使用者資料，公司ID:', companyid);

    const params = new HttpParams().set('companyId', companyid);

    return this.http.get<any>(`${this.userApiUrl}/getusers`, { params }).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && response.data && Array.isArray(response.data)) {
          console.log('從 API 成功獲取使用者資料，數量:', response.data.length);

          // 將 API 回應轉換為 User 格式
          const users: User[] = response.data.map((user: any) => ({
            id: user.id,
            name: user.username || user.email || 'Unknown User',
            departmentId: user.departmentId || null, // API 可能沒有這個欄位
            email: user.email || '',
            position: user.position || ''
          }));

          console.log('轉換後的使用者資料:', users);
          return users;
        }

        console.warn('API 回應格式不正確或無資料:', response);
        return [];
      }),
      catchError(error => {
        console.error('獲取使用者資料失敗:', error);
        return of([]);
      })
    );
  }

  // 新增方法：提交簽核結果並更新表單資料
  submitApprovalAndUpdateForm(formData: {
    formId: string;
    companyid: string;
    stepIndex: number;
    approvalData: any;
  }): Observable<any> {
    const { formId, companyid, stepIndex, approvalData } = formData;

    console.log('提交簽核結果並更新表單資料:', {
      formId,
      companyid,
      stepIndex,
      approvalData
    });

    // 呼叫新的 API 來更新簽核流程步驟
    return this.http.put<any>(`${environment.apiUrl}/api/form-manager/update_approval_steps`, {
      formId,
      companyid,
      stepIndex,
      approvalData
    }).pipe(
      map(response => {
        console.log('簽核流程步驟更新成功:', response);
        return response;
      }),
      catchError(error => {
        console.error('提交簽核結果失敗:', error);
        throw error;
      })
    );
  }

  // 新增方法：取得表單完整資料（包含簽核結果）
  getFormWithApprovalStatus(formId: string, companyid: string): Observable<any> {
    console.log('取得表單完整資料，包含簽核狀態:', { formId, companyid });

    const params = new HttpParams().set('companyid', companyid);

    return this.http.get<any>(`${environment.apiUrl}/api/forms/${formId}`, { params }).pipe(
      map(response => {
        console.log('成功取得表單資料:', response);
        return response;
      }),
      catchError(error => {
        console.error('取得表單資料失敗:', error);
        return of(null);
      })
    );
  }
}
